import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import SiBarChart from './SiChart';
import SiItemDetail from './SiItemDetail';
import SiItemsBreakdown from './SiItemsBreakdown';
import SiItemSearch from './SiItemSearch';
import { fmtDateTime } from '../timefmt';

export const FC_PERIODS = [['30d', '30 days'], ['3m', '3 months'], ['6m', '6 months'], ['ytd', 'Year to date'], ['12m', '12 months']];

const YTD_DAYS = Math.max(1, Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000) + 1);
export const PERIODS = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [YTD_DAYS, 'Year to date'], [365, '12 months']];

const fmtMoney = (n, cur) => (cur === 'USD' ? 'US$' : 'A$') + Math.round(n || 0).toLocaleString();
const fmtQty = (n) => Math.round(n || 0).toLocaleString();
const fmtDate = (iso) => fmtDateTime(iso);

const DEFAULT_SECTIONS = { overall: true, forecast: true, stale: true, items: true, itemForecast: false };

export default function SalesIntelSection({ toast, days: daysProp, onDaysChange, compact, goView, segment = '', subsidiary = '', focus = 'combined', sections = DEFAULT_SECTIONS, bare = false, canSync }) {
  const [daysState, setDaysState] = useState(30);
  const days = daysProp != null ? daysProp : daysState;
  const setDays = onDaysChange || setDaysState;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fc, setFc] = useState(null);
  const [stale, setStale] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showAllStale, setShowAllStale] = useState(false);
  const [drill, setDrill] = useState(null); // {kind: 'rep'|'client', name, role?}
  const [showAllItems, setShowAllItems] = useState(false);
  const [picked, setPicked] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const togglePick = (k) => setPicked(x => (x.includes(k) ? x.filter(i => i !== k) : [...x, k]));
  const [fcPeriod, setFcPeriod] = useState('12m');
  const [fcItems, setFcItems] = useState(12);
  const [buffer, setBufferState] = useState(() => { const v = parseFloat(localStorage.getItem('si_reorder_buffer')); return isNaN(v) ? 20 : v; });
  const setBuffer = (v) => { const n = Math.min(300, Math.max(0, Number(v) || 0)); setBufferState(n); localStorage.setItem('si_reorder_buffer', String(n)); };
  const pollRef = useRef(null);

  const load = useCallback(() => {
    api.salesIntelSummary(days, 60, segment, subsidiary).then(d => { setData(d); setErr(null); }).catch(e => setErr(e.response?.data?.detail || e.message));
  }, [days, segment, subsidiary]);
  useEffect(() => { load(); setShowAllItems(false); }, [load]);
  const loadForecast = useCallback(() => {
    if (sections.forecast || sections.itemForecast) api.salesIntelForecast(segment, subsidiary, fcPeriod, sections.itemForecast ? fcItems : 12, buffer).then(setFc).catch(() => {});
    if (sections.stale) api.salesIntelStale(30, segment, subsidiary).then(setStale).catch(() => {});
  }, [segment, subsidiary, fcPeriod, fcItems, buffer, sections.forecast, sections.itemForecast, sections.stale]);
  useEffect(() => { loadForecast(); }, [loadForecast]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  function pollUntilDone() {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const m = await api.salesIntelStatus();
        if (!m.running) {
          clearInterval(pollRef.current);
          setBusy(false);
          if (m.error) toast('Sales data sync failed: ' + m.error, true);
          else toast('Sales data refreshed from NetSuite');
          load();
          loadForecast();
        } else {
          setData(d => d ? { ...d, running: true, progress: m.progress } : d);
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
  }

  async function refresh() {
    setBusy(true);
    try {
      await api.salesIntelSync();
      setData(d => d ? { ...d, running: true, progress: 'Starting sync…' } : d);
      pollUntilDone();
    } catch (e) {
      setBusy(false);
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 409) { pollUntilDone(); setBusy(true); }
      else toast('Refresh failed: ' + detail, true);
    }
  }

  async function exportXlsx() {
    setExporting(true);
    try {
      const blob = await api.salesIntelExport(days, segment, subsidiary);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sales-intelligence-' + (segment ? segment.toLowerCase() + '-' : '') + (days === 365 ? '12mo' : days + 'd') + '.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast('Spreadsheet downloaded — every table is a separate sheet');
    } catch (e) { toast('Export failed: ' + (e.response?.data?.detail || e.message), true); }
    setExporting(false);
  }

  if (err) return null;
  if (!data) return <section className="dash-card si-card" data-testid="sales-intel"><div className="empty-note">Loading sales intelligence…</div></section>;

  const q = data.quotes;
  const o = data.orders;
  const cur = data.currency;
  const running = data.running || busy;
  const isQ = focus === 'quotes', isO = focus === 'orders', isC = !isQ && !isO;
  const allItems = isO
    ? [...(data.top_items || [])].sort((a, b) => (b.ordered_qty || 0) - (a.ordered_qty || 0))
    : (data.top_items || []);
  const visibleItems = showAllItems ? allItems : allItems.slice(0, 10);
  const avgOrder = o && o.total_count ? o.total_amount / o.total_count : null;

  if (compact) {
    return (
      <section className="dash-card si-card" data-testid="sales-intel-compact">
        <div className="dash-card-head si-head">
          <h3>Sales Intelligence <span className="dash-flag-tag si-tag">Management</span></h3>
          <button className="btn btn-sm btn-primary" data-testid="si-open-page" onClick={() => goView && goView('salesintel')}>Open Sales Intelligence →</button>
        </div>
        <div className="si-sub">
          {running ? <span className="si-running">● {data.progress || 'Sync in progress…'}</span>
            : data.last_sync ? <span>Last 30 days · synced {fmtDate(data.last_sync)}</span> : <span>No data yet — open the page and run the first sync.</span>}
        </div>
        <div className="si-kpis" data-testid="si-kpis">
          <div className="si-kpi"><span className="si-kpi-num">{q ? q.total_count.toLocaleString() : '—'}</span><span className="si-kpi-lbl">Quotes raised</span></div>
          <div className="si-kpi"><span className="si-kpi-num">{q ? fmtMoney(q.total_amount, cur) : '—'}</span><span className="si-kpi-lbl">Total quoted</span></div>
          <div className="si-kpi"><span className="si-kpi-num">{q && q.true_win_pct != null ? q.true_win_pct + '%' : '—'}</span><span className="si-kpi-lbl">True wins</span></div>
          <div className="si-kpi"><span className="si-kpi-num">{o ? o.total_count.toLocaleString() : '—'}</span><span className="si-kpi-lbl">Sales orders{o ? ' · ' + fmtMoney(o.total_amount, cur) : ''}</span></div>
        </div>
        {stale && stale.total > 0 && (
          <div className="si-notice" style={{ marginBottom: 0 }}>⏰ {stale.total} open quote{stale.total === 1 ? '' : 's'} idle 30+ days ({fmtMoney(stale.total_amount, cur)}) — details on the Sales Intelligence page</div>
        )}
      </section>
    );
  }

  const showKpis = sections.kpis != null ? sections.kpis : !!sections.overall;
  const showTables = sections.tables != null ? sections.tables : !!sections.overall;
  const allowSync = canSync != null ? canSync : !!sections.overall;
  const syncLine = running
    ? <span className="si-running" data-testid="si-progress">● {data.progress || 'Sync in progress…'}</span>
    : data.last_sync
      ? <span>Auto-synced every 24h from NetSuite · last pull {fmtDate(data.last_sync)}</span>
      : <span>No data yet — hit “Refresh from NetSuite” to run the first 12-month pull.</span>;
  const syncActions = allowSync && (
    <div className="si-controls">
      <button className="btn btn-sm" data-testid="si-export-btn" disabled={exporting} onClick={exportXlsx}>
        {exporting ? 'Exporting…' : '⬇ Export Excel'}
      </button>
      <button className="btn btn-sm" data-testid="si-refresh-btn" disabled={running} onClick={refresh}>
        {running ? 'Syncing…' : '↻ Refresh from NetSuite'}
      </button>
    </div>
  );

  return (
    <section className={'dash-card si-card' + (bare ? ' si-bare' : '')} data-testid="sales-intel">
      {bare ? (
        <div className="si-syncbar" data-testid="si-syncbar">
          <span className="si-sub" style={{ margin: 0 }}>{syncLine}</span>
          {syncActions}
        </div>
      ) : (
        <>
          <div className="dash-card-head si-head">
            <h3>Sales Intelligence <span className="dash-flag-tag si-tag">Management</span></h3>
            <div className="si-controls">
              {PERIODS.map(([d, l]) => (
                <button key={l} className={'si-period' + (days === d ? ' on' : '')} data-testid={'si-period-' + (l === 'Year to date' ? 'ytd' : d)} onClick={() => setDays(d)}>{l}</button>
              ))}
              {syncActions}
            </div>
          </div>
          <div className="si-sub">{syncLine}</div>
        </>
      )}

      {showKpis && <>
      <div className="si-kpis" data-testid="si-kpis">
        {!isO && <div className="si-kpi"><span className="si-kpi-num">{q ? q.total_count.toLocaleString() : '—'}</span><span className="si-kpi-lbl">Quotes raised</span></div>}
        {!isO && <div className="si-kpi"><span className="si-kpi-num">{q ? fmtMoney(q.total_amount, cur) : '—'}</span><span className="si-kpi-lbl">Total quoted</span></div>}
        {isQ && <div className="si-kpi"><span className="si-kpi-num">{q && q.avg_gm_pct != null ? q.avg_gm_pct + '%' : '—'}</span><span className="si-kpi-lbl" title="Unweighted average of NetSuite's estimated GM% on each quote">Avg GM% est.</span></div>}
        {data.margin && data.margin.available && (
          <div className="si-kpi" data-testid="si-kpi-gm-amort">
            <span className="si-kpi-num">{data.margin.gm_amort_pct != null ? data.margin.gm_amort_pct + '%' : '—'}</span>
            <span className="si-kpi-lbl" title={'Value-weighted margin on sales orders with freight amortised. Goods-only GM ' + data.margin.gm_goods_pct + '%, freight is ' + data.margin.freight_share_pct + '% of revenue and costs ' + data.margin.freight_drag_pp + 'pp of margin. Freight cost source: ' + data.margin.freight_cost_source}>
              GM% freight-amortised{data.margin.freight_drag_pp ? ' · −' + data.margin.freight_drag_pp + 'pp' : ''}
            </span>
          </div>
        )}
        {!isO && (
          <div className="si-kpi" data-testid="si-kpi-truewin">
            <span className="si-kpi-num">{q && q.true_win_pct != null ? q.true_win_pct + '%' : '—'}</span>
            <span className="si-kpi-lbl" title={'Opportunities that became sales orders' + (q && q.opp_count ? ' — ' + q.true_win_count + ' of ' + q.opp_count + ' opportunities' : '')}>True wins (opportunity → order){q && q.avg_days_to_close != null ? ' · ' + Math.round(q.avg_days_to_close) + 'd to close' : ''}</span>
          </div>
        )}
        {!isQ && (
          <div className="si-kpi" data-testid="si-kpi-orders">
            <span className="si-kpi-num">{o ? o.total_count.toLocaleString() : '—'}</span>
            <span className="si-kpi-lbl">Sales orders</span>
          </div>
        )}
        {!isQ && <div className="si-kpi"><span className="si-kpi-num">{o ? fmtMoney(o.total_amount, cur) : '—'}</span><span className="si-kpi-lbl">Order value</span></div>}
        {isO && <div className="si-kpi" data-testid="si-kpi-avg-order"><span className="si-kpi-num">{avgOrder != null ? fmtMoney(avgOrder, cur) : '—'}</span><span className="si-kpi-lbl">Avg order value</span></div>}
        {isO && (
          <div className="si-kpi" data-testid="si-kpi-fromquotes">
            <span className="si-kpi-num">{q && q.true_win_count != null ? q.true_win_count.toLocaleString() : '—'}</span>
            <span className="si-kpi-lbl">Orders from opportunities{q && q.true_win_amount ? ' · ' + fmtMoney(q.true_win_amount, cur) : ''}</span>
          </div>
        )}
      </div>

      {!data.so_available && data.so_note && (
        <div className="si-notice" data-testid="si-so-notice">⚠ {data.so_note}</div>
      )}

      {!isO && q && <DailyChart daily={q.daily} days={days} cur={cur} label="Quotes" unit="quote" />}
      {!isQ && o && <DailyChart daily={o.daily} days={days} cur={cur} label="Sales orders" unit="order" />}
      </>}

      {showTables && <>
      {isO ? (
        o ? (
          <>
            <div className="si-grid">
              <SiTable testid="si-orders-by-rep" title="Orders by sales rep" rows={o.by_rep} cur={cur} showGroup
                onPick={n => setDrill({ kind: 'rep', name: n, role: 'rep' })} />
              <SiTable testid="si-orders-by-creator" title="Orders by creator" rows={o.by_creator} cur={cur} showGroup
                onPick={n => setDrill({ kind: 'rep', name: n, role: 'creator' })} />
              <SiTable testid="si-by-group" title="By team group" rows={(data.group_totals || []).map(g => ({ name: g.group, count: g.count, amount: g.amount }))} cur={cur} />
            </div>
            <div className="si-grid">
              <SiTable testid="si-top-clients-ordered" title="Top clients (ordered)" rows={o.top_clients} cur={cur} linkPrefix="si-client-link-"
                onPick={n => setDrill({ kind: 'client', name: n })} />
              {(o.top_parents || []).length > 0 && (
                <SiTable testid="si-top-parents-ordered" title="By parent company (retail)" rows={o.top_parents} cur={cur}
                  footNote="Sales orders rolled up to the NetSuite parent company" />
              )}
            </div>
          </>
        ) : (
          <div className="empty-note" data-testid="si-no-order-data">No sales order data for this period{data.last_sync ? '' : ' — run the first sync'}.</div>
        )
      ) : q ? (
        <>
          <div className="si-grid">
            <SiTable testid="si-by-creator" title="Quotes by creator" rows={q.by_creator} cur={cur} showGroup
              onPick={n => setDrill({ kind: 'rep', name: n, role: 'creator' })} />
            <SiTable testid="si-by-rep" title="Quotes by sales rep" rows={q.by_rep} cur={cur} showGroup
              onPick={n => setDrill({ kind: 'rep', name: n, role: 'rep' })} />
            <SiTable testid="si-by-group" title="By team group" rows={(data.group_totals || []).map(g => ({ name: g.group, count: g.count, amount: g.amount }))} cur={cur}
              footNote="Groups auto-assign from NetSuite roles — override in Admin → Sales Team Groups" />
          </div>
          <div className="si-grid">
            <SiTable testid="si-top-clients" title="Top clients (quoted)" rows={q.top_clients} cur={cur} linkPrefix="si-client-link-"
              onPick={n => setDrill({ kind: 'client', name: n })} />
            {isC && o && (
              <SiTable testid="si-top-clients-ordered" title="Top clients (ordered)" rows={o.top_clients} cur={cur} linkPrefix="si-oclient-link-"
                onPick={n => setDrill({ kind: 'client', name: n })} />
            )}
            {((isC && o && (o.top_parents || []).length > 0) || (q.top_parents || []).length > 0) && (
              <SiTable testid="si-top-parents" title="By parent company (retail)"
                rows={(isC && o && (o.top_parents || []).length > 0) ? o.top_parents : q.top_parents} cur={cur}
                footNote={isC && o && (o.top_parents || []).length > 0 ? 'Sales orders rolled up to the NetSuite parent company' : 'Quotes rolled up to the NetSuite parent company'} />
            )}
          </div>
        </>
      ) : (
        <div className="empty-note" data-testid="si-no-data">No quote data for this period{data.last_sync ? '' : ' — run the first sync'}.</div>
      )}
      </>}

      {sections.forecast && fc && (fc.quotes || fc.orders)
        && <SalesForecastPanel fc={fc} focus={focus} period={fcPeriod} onPeriod={setFcPeriod} />}

      {sections.stale && !isO && stale && stale.total > 0 && (
        <div className="si-stale" data-testid="si-stale-quotes">
          <div className="si-stale-head">
            <h4>⏰ Stale open quotes — {stale.days_idle}+ days without activity</h4>
            <span className="si-stale-count" data-testid="si-stale-count">{stale.total.toLocaleString()} quote{stale.total === 1 ? '' : 's'} · {fmtMoney(stale.total_amount, cur)} sitting idle</span>
          </div>
          <table className="si-table si-items-table">
            <thead><tr><th>Quote #</th><th>Created</th><th>Last activity</th><th className="num">Days idle</th><th>Rep</th><th>Client</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {(showAllStale ? stale.items : stale.items.slice(0, 8)).map(sq => (
                <tr key={sq.tranid + sq.trandate} data-testid={'si-stale-' + sq.tranid}>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{sq.tranid}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{sq.trandate}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{sq.lastmod}</td>
                  <td className="num"><span className={'si-idle' + (sq.idle_days >= 90 ? ' bad' : '')}>{sq.idle_days}</span></td>
                  <td className="si-item-name" title={sq.salesrep}>{sq.salesrep || '—'}</td>
                  <td className="si-item-name" title={sq.client}>{sq.client}</td>
                  <td className="num strong">{fmtMoney(sq.amount, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {stale.items.length > 8 && (
            <button className="si-expand" data-testid="si-stale-expand" onClick={() => setShowAllStale(v => !v)}>
              {showAllStale ? '▴ Show fewest' : `▾ Show ${stale.items.length < stale.total ? 'oldest ' + stale.items.length : 'all ' + stale.items.length}`}
            </button>
          )}
        </div>
      )}

      {running && allItems.length === 0 && (
        <div className="empty-note" data-testid="si-items-loading">Line items, item trends and stock load in the final stage of the sync — they'll appear when it finishes (first full sync takes ~3–5 minutes).</div>
      )}

      {sections.items && allItems.length > 0 && (
        <div className="si-items" data-testid="si-top-items">
          <h4>{isQ ? 'Most quoted items — demand vs stock' : isO ? 'Most ordered items — sold vs stock' : 'Items — quoted vs ordered vs stock'} <span className="si-fc-note">click an item for quotes, sales orders &amp; POs by period · tick several to combine them as one</span></h4>
          {picked.length > 0 && (
            <div className="si-bd-bar" data-testid="si-items-selection-bar">
              <b data-testid="si-items-selected-count">{picked.length} selected</b>
              <button className="btn btn-sm btn-primary" data-testid="si-items-view-combined" onClick={() => setBreakdown(picked)}>View combined totals</button>
              <button className="btn btn-sm btn-ghost" data-testid="si-items-clear-selection" onClick={() => setPicked([])}>Clear</button>
            </div>
          )}
          <SiItemSearch picked={picked} onToggle={togglePick} onOpen={(k) => setBreakdown([k])} />
          <table className="si-table si-items-table">
            <thead><tr>
              <th className="si-pick-col" />
              <th>Item</th>
              {!isO && <th className="num">Quoted qty</th>}
              {!isO && <th className="num">Quoted $</th>}
              {!isQ && <th className="num">Ordered qty</th>}
              {!isQ && <th className="num">Ordered $</th>}
              {isC && <th className="num">Q→O %</th>}
              <th className="num">On hand</th>
              <th>Stock</th>
            </tr></thead>
            <tbody>
              {visibleItems.map(it => (
                <tr key={it.item_id} data-testid={'si-item-' + it.item_id} className="si-item-row" onClick={() => setBreakdown([it.item_id])} title="Open quotes, sales orders and POs for this item">
                  <td className="si-pick-col" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" data-testid={'si-item-pick-' + it.item_id} checked={picked.includes(it.item_id)} onChange={() => togglePick(it.item_id)} aria-label={'Select ' + it.itemid} />
                  </td>
                  <td className="si-item-name" title={it.name}>
                    {it.itemid}
                    {it.from_generic && <span className="si-gen-pill" title={'Resolved from a Generic Budgetary line display name: ' + it.name}>from generic</span>}
                  </td>
                  {!isO && <td className="num">{fmtQty(it.quoted_qty)}</td>}
                  {!isO && <td className="num">{fmtMoney(it.quoted_amount, cur)}</td>}
                  {!isQ && <td className="num">{it.ordered_qty ? fmtQty(it.ordered_qty) : '—'}</td>}
                  {!isQ && <td className="num">{it.ordered_amount ? fmtMoney(it.ordered_amount, cur) : '—'}</td>}
                  {isC && <td className="num">{it.quoted_qty > 0 ? Math.round((it.ordered_qty / it.quoted_qty) * 100) + '%' : '—'}</td>}
                  <td className="num">{it.on_hand != null ? fmtQty(it.on_hand) : '—'}</td>
                  <td>{it.on_hand == null ? <span className="si-stock-na">n/a</span>
                    : it.low_stock ? <span className="si-stock-low" data-testid={'si-low-stock-' + it.item_id}>⚠ below demand</span>
                    : <span className="si-stock-ok">✓ covered</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {allItems.length > 10 && (
            <button className="si-expand" data-testid="si-items-expand" onClick={() => setShowAllItems(v => !v)}>
              {showAllItems ? '▴ Show top 10 only' : `▾ Show all ${allItems.length} items`}
            </button>
          )}
        </div>
      )}

      {breakdown && <SiItemsBreakdown items={breakdown} segment={segment} subsidiary={subsidiary} onClose={() => setBreakdown(null)} />}

      {sections.itemForecast && fc && (fc.items || []).length > 0
        && <ItemForecastPanel fc={fc} focus={focus} period={fcPeriod} onPeriod={setFcPeriod} segment={segment} subsidiary={subsidiary}
          limit={fcItems} onLimit={setFcItems} toast={toast} buffer={buffer} onBuffer={setBuffer} />}

      {drill && <DrillModal drill={drill} days={days} segment={segment} subsidiary={subsidiary} onClose={() => setDrill(null)} />}
    </section>
  );
}

function DailyChart({ daily, days, cur, label = 'Quotes', unit = 'quote' }) {
  let buckets = daily || [];
  if (days > 95 && buckets.length) {
    const byMonth = {};
    buckets.forEach(d => {
      const k = (d.date || '').slice(0, 7);
      byMonth[k] = byMonth[k] || { date: k, count: 0, amount: 0 };
      byMonth[k].count += d.count; byMonth[k].amount += d.amount;
    });
    buckets = Object.values(byMonth).sort((a, b) => a.date.localeCompare(b.date));
  }
  if (!buckets.length) return null;
  return (
    <SiBarChart testid={'si-chart-' + unit} cur={cur} unit={unit}
      tone={unit === 'order' ? 'amber' : 'navy'}
      title={label + ' per ' + (days > 95 ? 'month' : 'day')}
      buckets={buckets.map(b => ({ key: b.date, count: b.count, amount: b.amount }))} />
  );
}

function SiTable({ title, rows, cur, showGroup, footNote, testid, onPick, linkPrefix }) {
  const [all, setAll] = useState(false);
  const shown = all ? (rows || []) : (rows || []).slice(0, 8);
  return (
    <div className="si-panel" data-testid={testid}>
      <h4>{title}</h4>
      {(rows || []).length === 0 ? <div className="empty-note" style={{ border: 'none' }}>No data.</div> : (
        <table className="si-table">
          <tbody>
            {shown.map(r => (
              <tr key={r.name}>
                <td className="si-item-name" title={onPick ? 'Open ' + r.name + "'s breakdown" : r.name}>
                  <span className="si-cell-flex">
                    {onPick
                      ? <button className="si-name-link" data-testid={(linkPrefix || 'si-rep-link-') + r.name.replace(/\s+/g, '-')} onClick={() => onPick(r.name)}>{r.name}</button>
                      : <span className="si-cell-name">{r.name}</span>}
                    {showGroup && r.group && r.group !== 'Unassigned' && <span className="si-group-pill">{r.group}</span>}
                  </span>
                </td>
                <td className="num">{r.count}</td>
                <td className="num strong">{fmtMoney(r.amount, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(rows || []).length > 8 && (
        <button className="si-expand" data-testid={testid + '-expand'} onClick={() => setAll(v => !v)}>
          {all ? '▴ Show top 8' : `▾ Show all ${rows.length}`}
        </button>
      )}
      {footNote && <div className="field-hint" style={{ marginTop: 8 }}>{footNote}</div>}
    </div>
  );
}

function SalesForecastPanel({ fc, focus = 'combined', period = '12m', onPeriod }) {
  const cur = fc.currency;
  const q = fc.quotes;
  const o = fc.orders;
  const opp = fc.opp_pipeline;
  const isQ = focus === 'quotes', isO = focus === 'orders';
  const per = fc.bucket === 'week' ? 'week' : 'month';
  const horizon = fc.next_label || 'next month';
  const conv = q && o && q.forecast_amount ? Math.round((o.forecast_amount / q.forecast_amount) * 1000) / 10 : null;
  return (
    <div className="si-forecast" data-testid="si-forecast">
      <div className="si-forecast-head">
        <h4>Sales/Quote Forecasts — {horizon}</h4>
        <PeriodButtons period={period} onPeriod={onPeriod} />
      </div>
      <div className="si-forecast-basis" data-testid="si-fc-basis">
        Based on {fc.period_label ? fc.period_label.toLowerCase() : 'history'} ({fc.period_start} → {fc.period_end}) · {(isO ? o?.basis : q?.basis) || ''} · projected one {per} at a time, {fc.buckets} {per}s ahead
      </div>
      <div className="si-fc-groups">
        {!isO && q && <FcGroup testid="si-fc-quotes" title="Quotes" tone="navy" t={q} cur={cur} per={per} horizon={horizon} unit="quote" />}
        {!isQ && (o
          ? <FcGroup testid="si-fc-orders" title="Sales orders" tone="amber" t={o} cur={cur} per={per} horizon={horizon} unit="order" />
          : <div className="si-fc-kpi"><span className="si-fc-num">—</span><span className="si-fc-lbl">Sales orders — needs NetSuite permission</span></div>)}
      </div>
      {!isQ && !isO && q && o && (
        <div className="si-fc-exec" data-testid="si-fc-exec">
          <div className="si-fc-kpi"><span className="si-fc-num">{conv != null ? conv + '%' : '—'}</span><span className="si-fc-lbl">Implied quote → order value conversion ({fmtMoney(o.forecast_amount, cur)} of {fmtMoney(q.forecast_amount, cur)} expected)</span></div>
          <div className="si-fc-kpi"><span className="si-fc-num">{q.avg_value != null ? fmtMoney(q.avg_value, cur) : '—'}</span><span className="si-fc-lbl">Avg quote value · <b>{o.avg_value != null ? fmtMoney(o.avg_value, cur) : '—'}</b> avg order value</span></div>
          <div className="si-fc-kpi"><span className="si-fc-num">{fmtMoney(q.actual_amount - o.actual_amount, cur)}</span><span className="si-fc-lbl">Quoted but not ordered in the period ({fmtQty(q.actual_count - o.actual_count)} more quotes than orders)</span></div>
        </div>
      )}
      {!isO && q && <ForecastChart history={q.history} projection={q.projection} cur={cur} label="Quotes" per={per} horizon={horizon} />}
      {!isQ && o && <ForecastChart history={o.history} projection={o.projection} cur={cur} label="Sales orders" per={per} horizon={horizon} order />}
      {opp && opp.open_count > 0 && (
        <div className="si-panel" data-testid="si-fc-opp-pipeline" style={{ margin: '10px 0' }}>
          <h4>Probability-weighted opportunity pipeline <span className="si-forecast-basis">from the Win Probability model — shown separately so it never double-counts the quote/order forecast above</span></h4>
          <div className="si-fc-kpis" style={{ marginBottom: 0 }}>
            <div className="si-fc-kpi"><span className="si-fc-num" data-testid="si-fc-opp-expected">{fmtMoney(opp.expected_value, cur)}</span><span className="si-fc-lbl">Expected value (Σ value × win probability)</span></div>
            <div className="si-fc-kpi"><span className="si-fc-num">{fmtMoney(opp.open_value, cur)}</span><span className="si-fc-lbl">Open opportunity value ({(opp.open_count || 0).toLocaleString()} opps)</span></div>
            <div className="si-fc-kpi"><span className="si-fc-num">{opp.blended_pct != null ? opp.blended_pct + '%' : '—'}</span><span className="si-fc-lbl">Blended win likelihood</span></div>
          </div>
        </div>
      )}
      {!isO && q && q.accuracy && <AccuracyStrip acc={q.accuracy} cur={cur} per={per} label="Quotes" testid="si-forecast-accuracy" />}
      {!isQ && o && o.accuracy && <AccuracyStrip acc={o.accuracy} cur={cur} per={per} label="Sales orders" testid="si-forecast-accuracy-orders" />}
    </div>
  );
}

function PeriodButtons({ period, onPeriod, prefix = 'si-fc-period-' }) {
  return (
    <div className="si-controls" data-testid={prefix + 'group'}>
      {FC_PERIODS.map(([k, l]) => (
        <button key={k} className={'si-period' + (period === k ? ' on' : '')}
          data-testid={prefix + k} onClick={() => onPeriod && onPeriod(k)}>{l}</button>
      ))}
    </div>
  );
}

const Trend = ({ v }) => v == null ? <span className="si-trend flat">—</span>
  : <span className={'si-trend ' + (v > 2 ? 'up' : v < -2 ? 'down' : 'flat')}>{v > 0 ? '▲' : v < 0 ? '▼' : '■'} {Math.abs(v) >= 999 ? '999%+' : Math.abs(v).toFixed(1) + '%'}</span>;

function FcGroup({ testid, title, tone, t, cur, per, horizon, unit }) {
  return (
    <div className={'si-fc-group ' + tone} data-testid={testid}>
      <div className="si-fc-group-title"><i className={'sw ' + tone} />{title} · {horizon}</div>
      <div className="si-fc-kpis">
        <div className="si-fc-kpi"><span className="si-fc-num" data-testid={testid + '-count'}>{t.forecast_count.toLocaleString()}</span><span className="si-fc-lbl">{title} expected · {t.per_bucket_count.toLocaleString()} per {per}</span></div>
        <div className="si-fc-kpi"><span className="si-fc-num" data-testid={testid + '-value'}>{fmtMoney(t.forecast_amount, cur)}</span><span className="si-fc-lbl">Value expected · {fmtMoney(t.per_bucket_amount, cur)} per {per}</span></div>
        <div className="si-fc-kpi"><span className="si-fc-num"><Trend v={t.vs_actual_pct} /></span><span className="si-fc-lbl">vs the {fmtMoney(t.actual_amount, cur)} actually {unit === 'order' ? 'ordered' : 'quoted'} in the period</span></div>
        <div className="si-fc-kpi"><span className="si-fc-num"><Trend v={t.momentum_amount_pct} /></span><span className="si-fc-lbl">Momentum · newest half of the period vs older half (value){t.momentum_count_pct != null ? ` · ${t.momentum_count_pct > 0 ? '+' : ''}${t.momentum_count_pct}% by count` : ''}</span></div>
        {t.best_bucket && <div className="si-fc-kpi"><span className="si-fc-num">{t.best_bucket.month}</span><span className="si-fc-lbl">Best {per} · {fmtMoney(t.best_bucket.amount, cur)} across {t.best_bucket.count.toLocaleString()} {unit}s</span></div>}
      </div>
    </div>
  );
}

function ItemForecastPanel({ fc, focus = 'combined', period = '12m', onPeriod, segment, subsidiary, limit = 12, onLimit, toast, buffer = 20, onBuffer }) {
  const [bufDraft, setBufDraft] = useState(String(buffer));
  useEffect(() => { setBufDraft(String(buffer)); }, [buffer]);
  const commitBuffer = () => { if (bufDraft !== '' && Number(bufDraft) !== buffer) onBuffer && onBuffer(bufDraft); };
  const all = fc.items || [];
  const [item, setItem] = useState(null);
  const [search, setSearch] = useState('');
  const [shortOnly, setShortOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const per = fc.bucket === 'week' ? 'week' : 'month';
  const needle = search.trim().toLowerCase();
  let items = needle ? all.filter(it => (it.itemid + ' ' + (it.name || '')).toLowerCase().includes(needle)) : all;
  if (shortOnly) items = items.filter(it => it.reorder_qty > 0).sort((a, b) => b.reorder_qty - a.reorder_qty);
  const total = fc.items_total || all.length;
  const shortCount = all.filter(it => it.reorder_qty > 0).length;
  const reorderUnits = all.reduce((a, it) => a + (it.reorder_qty || 0), 0);
  if (all.length === 0) return null;

  function toggleShort() {
    const on = !shortOnly;
    setShortOnly(on);
    if (on && limit <= 12 && onLimit) onLimit(300);
  }
  async function exportXlsx() {
    setExporting(true);
    try {
      const blob = await api.salesIntelItemForecastExport(period, segment, subsidiary, buffer);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'item-forecast-' + period + (segment ? '-' + segment.toLowerCase() : '') + '.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast && toast('Item forecast downloaded — "Reorder list" sheet first, then every item');
    } catch (e) { toast && toast('Export failed: ' + (e.response?.data?.detail || e.message), true); }
    setExporting(false);
  }

  return (
    <div className="si-forecast si-fc-items" data-testid="si-item-forecast">
      <div className="si-forecast-head">
        <h4>Item demand forecast — {fc.next_label || 'next month'}</h4>
        <PeriodButtons period={period} onPeriod={onPeriod} prefix="si-if-period-" />
        <button className="btn btn-sm" data-testid="si-if-export" disabled={exporting} onClick={exportXlsx}
          title="Excel: every item with demand in the period, with forecast, stock and shortfall">{exporting ? 'Building…' : '⬇ Export all items'}</button>
      </div>
      <div className="si-forecast-basis" data-testid="si-if-basis">
        Based on {fc.period_label ? fc.period_label.toLowerCase() : 'history'} ({fc.period_start} → {fc.period_end}) · weighted average of {fc.buckets} {per}(s) of ORDERED quantity, newest weighs most · outliers stay in unless you untick them in the drilldown
      </div>
      <div className="si-if-toolbar">
        <h5>{shortOnly ? `Items needing reorder (${items.length}${limit <= 12 ? ' of top 12' : ''})` : limit > 12 ? `All ${all.length} items by ordered demand` : 'Top 12 items by ordered demand'} <span className="si-fc-note">click an item for order history, outliers and stock</span></h5>
        <div className="si-if-tools">
          <label className="si-if-buffer" data-testid="si-if-buffer" title="Safety buffer added on top of the forecast before working out what to reorder">
            Safety buffer
            <input type="number" min="0" max="300" step="5" className="input" value={bufDraft}
              data-testid="si-if-buffer-input" aria-label="Safety buffer percent"
              onChange={e => setBufDraft(e.target.value)} onBlur={commitBuffer}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitBuffer(); e.target.blur(); } }} />%
          </label>
          <button className={'si-period' + (shortOnly ? ' on short' : '')} data-testid="si-if-short-only" onClick={toggleShort}
            title="Only items with a suggested reorder quantity">⚠ Needs reorder only{shortCount ? ` (${shortCount})` : ''}</button>
          <input className="input si-if-search" data-testid="si-if-search" placeholder="Filter items…" value={search}
            onChange={e => setSearch(e.target.value)} aria-label="Filter forecast items" />
        </div>
      </div>
      <div className="si-if-summary" data-testid="si-if-reorder-summary">
        <b>{shortCount.toLocaleString()}</b> of {all.length.toLocaleString()} listed items need reordering · <b>{fmtQty(reorderUnits)}</b> units in total at a {buffer}% buffer{limit <= 12 && total > 12 ? ' (top 12 shown — expand or export for the full list)' : ''}
      </div>
      {items.length === 0 && <p className="si-fc-note" data-testid="si-if-no-match" style={{ marginLeft: 0 }}>{shortOnly ? 'Nothing needs reordering in this list.' : `No items match “${search}”${limit <= 12 && total > 12 ? ' in the top 12 — show all items to search the full list' : ''}.`}</p>}
      <table className="si-table si-items-table">
        <thead><tr>
          <th>Item</th>
          <th className="num">Sold in period</th>
          <th className="num">Avg per {per}</th>
          <th className="num">Forecast ordered</th>
          <th className="num">Momentum</th>
          <th className="num">On hand</th>
          <th className="num">Open on order</th>
          {fc.po_available && <th className="num" title="Open purchase order quantity still to be received (from NetSuite)">On PO</th>}
          <th>Supply</th>
          <th className="num" title={'Forecast × (1 + ' + buffer + '%) − (on hand + in transit + on PO − open on order), rounded up'}>Reorder</th>
        </tr></thead>
        <tbody>
          {items.map(it => (
            <tr key={it.item_id} data-testid={'si-fc-item-' + it.item_id} className="si-row-click"
              onClick={() => setItem(it.item_id)} title="Open the detailed breakdown">
              <td className="si-item-name" title={it.name}>
                {it.itemid}
                {it.from_generic && <span className="si-gen-pill" title={'Resolved from a Generic Budgetary line display name: ' + it.name}>from generic</span>}
              </td>
              <td className="num">{fmtQty(it.sold)}</td>
              <td className="num">{fmtQty(it.avg_monthly_ordered)}</td>
              <td className="num strong">
                {fmtQty(it.forecast_ordered)}
                {it.outlier_count > 0 && (
                  <span className="si-gen-pill si-pill-warn" data-testid={'si-fc-outliers-' + it.item_id}
                    title={it.outlier_count + ' outlier order(s) included — without them the forecast would be ' + fmtQty(it.forecast_ordered_clean)}>
                    {it.outlier_count} outlier{it.outlier_count === 1 ? '' : 's'} in
                  </span>
                )}
              </td>
              <td className="num"><Trend v={it.momentum_pct} /></td>
              <td className="num">{it.on_hand != null ? fmtQty(it.on_hand) : '—'}</td>
              <td className="num">{it.open_on_order != null ? fmtQty(it.open_on_order) : '—'}</td>
              {fc.po_available && <td className="num" data-testid={'si-fc-po-' + it.item_id}>{it.on_po != null ? fmtQty(it.on_po) : '—'}</td>}
              <td>{it.on_hand == null ? <span className="si-stock-na">n/a</span>
                : it.shortfall > 0 ? <span className="si-stock-low" data-testid={'si-fc-short-' + it.item_id}>⚠ short by {fmtQty(it.shortfall)}</span>
                : <span className="si-stock-ok">✓ covered</span>}</td>
              <td className="num">{it.reorder_qty == null ? <span className="si-stock-na">—</span>
                : it.reorder_qty > 0
                  ? <span className="si-reorder" data-testid={'si-fc-reorder-' + it.item_id}
                    title={'Target ' + fmtQty(it.target_qty) + ' (forecast ' + fmtQty(it.forecast_ordered) + ' + ' + buffer + '%) − available ' + fmtQty(it.available_supply) + ' (on hand ' + fmtQty(it.on_hand) + (it.in_transit ? ' + in transit ' + fmtQty(it.in_transit) : '') + (it.on_po ? ' + on PO ' + fmtQty(it.on_po) : '') + (it.open_on_order ? ' − open orders ' + fmtQty(it.open_on_order) : '') + ')'}>
                    {fmtQty(it.reorder_qty)}</span>
                  : <span className="si-stock-ok" title={'Available ' + fmtQty(it.available_supply) + ' already covers the target of ' + fmtQty(it.target_qty)}>0</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > 12 && (
        <button className="si-expand" data-testid="si-if-expand" onClick={() => onLimit && onLimit(limit > 12 ? 12 : 300)}>
          {limit > 12 ? '▴ Show top 12 only' : total > 300 ? `▾ Show top 300 of ${total.toLocaleString()} items with demand in the period` : `▾ Show all ${total.toLocaleString()} items with demand in the period`}
        </button>
      )}
      <p className="si-fc-note" style={{ marginLeft: 0, marginTop: 8 }}>Ranked by quoted + ordered demand. “Generic …” placeholder items have no stock record, so their supply reads n/a. Shortfall = forecast ordered − on hand. Open on order = committed + backordered on open sales orders. On PO = quantity still to be received on open purchase orders (updated on the nightly sync). Reorder = forecast × (1 + buffer) − (on hand + in transit + on PO − open on order), rounded up — hover a figure for the working. The Excel export covers every item, not just the top 300.</p>
      {!fc.po_available && fc.po_note && <p className="si-fc-note" data-testid="si-fc-po-note">⚠ {fc.po_note}</p>}
      {item && <SiItemDetail item={item} period={period} segment={segment} subsidiary={subsidiary} onClose={() => setItem(null)} />}
    </div>
  );
}

function ForecastChart({ history, projection, cur, label = 'Quotes', order = false, per = 'month', horizon }) {
  const bars = [...(history || []).map(h => ({ key: h.month, count: h.count, amount: h.amount })),
    ...(projection || []).map(p => ({ key: p.month, count: p.count, amount: p.amount, fc: true }))];
  if (!bars.length) return null;
  return (
    <SiBarChart testid={'si-fc-chart-' + (order ? 'order' : 'quote')} cur={cur}
      unit={order ? 'order' : 'quote'} tone={order ? 'amber' : 'navy'} buckets={bars}
      title={label + ' per ' + per + ' — actual, then projected ' + (horizon || '')}
      xLabel={bars[0].key + ' → ' + bars[bars.length - 1].key + ' (projected)'}
      footNote={'Striped bars are the weighted-average projection, one per ' + per + ' — see the Maths tab for the formula.'} />
  );
}

const STATUS_ORDER = ['Open', 'Processed', 'Closed', 'Expired'];
const STATUS_CLS = { Open: 'open', Processed: 'won', Closed: 'lost', Expired: 'lost' };

function AccuracyStrip({ acc, cur, per = 'month', label = 'Quotes', testid = 'si-forecast-accuracy' }) {
  const badge = (err) => {
    if (err == null) return null;
    const cls = err <= 15 ? 'won' : err <= 35 ? 'warn' : 'lost';
    return <span className={'si-acc-badge ' + cls}>{err <= 15 ? '✓' : '±'} {err}% off</span>;
  };
  return (
    <div className="si-acc" data-testid={testid}>
      <div className="si-acc-title">{label} model check — {acc.month} <span className="si-acc-note">predicted using only data available before that {per}</span></div>
      <div className="si-acc-row">
        <div className="si-acc-cell" data-testid={testid + '-count'}>
          <span className="si-fc-lbl">{label}</span>
          <span className="si-acc-vals">forecast <b>{acc.predicted_count.toLocaleString()}</b> · actual <b>{acc.actual_count.toLocaleString()}</b></span>
          {badge(acc.count_error_pct)}
        </div>
        <div className="si-acc-cell" data-testid={testid + '-value'}>
          <span className="si-fc-lbl">{label} value</span>
          <span className="si-acc-vals">forecast <b>{fmtMoney(acc.predicted_amount, cur)}</b> · actual <b>{fmtMoney(acc.actual_amount, cur)}</b></span>
          {badge(acc.amount_error_pct)}
        </div>
      </div>
    </div>
  );
}

function DrillModal({ drill, days, segment, subsidiary, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState('quotes');
  useEffect(() => {
    const p = drill.kind === 'client'
      ? api.salesIntelClient(drill.name, days, segment, subsidiary)
      : api.salesIntelRep(drill.name, drill.role, days, segment, subsidiary);
    p.then(setD).catch(e => setErr(e.response?.data?.detail || e.message));
  }, [drill, days, segment, subsidiary]);
  async function exportPerson(v) {
    setExporting(true);
    try {
      const blob = await api.salesIntelDrillExport(drill.kind, drill.name, drill.role || 'creator', days, segment, subsidiary, v);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sales-report-' + drill.name.replace(/\s+/g, '-').toLowerCase() + '-' + (v === 'both' ? 'all' : v) + '-' + (days === 365 ? '12mo' : days + 'd') + '.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* toast unavailable here */ }
    setExporting(false);
  }
  const cur = d?.currency || 'AUD';
  const isClient = drill.kind === 'client';
  const side = d ? (view === 'orders' ? d.orders : d.quotes) : null;
  const unit = view === 'orders' ? 'order' : 'quote';
  const statusOrder = view === 'orders'
    ? Object.keys(side?.by_status || {})
    : STATUS_ORDER.filter(s => side?.by_status?.[s]);
  return (
    <div className="modal-overlay" data-testid="si-drill-modal" onClick={onClose}>
      <div className="modal si-rep-modal" onClick={e => e.stopPropagation()}>
        <div className="si-rep-head">
          <div className="si-rep-title">
            <h3>{drill.name}</h3>
            <span className="si-rep-window">{isClient ? 'Client' : 'Rep'} · last {days === 365 ? '12 months' : days + ' days'}</span>
          </div>
          <div className="si-drill-exports">
            <button className="btn btn-sm" data-testid="si-drill-export" disabled={exporting || !d} onClick={() => exportPerson(view)}
              title={'Excel export of the ' + (view === 'orders' ? 'sales orders' : 'quotes') + ' shown — perfect for one-on-one reviews'}>
              {exporting ? 'Exporting…' : '⬇ Export ' + (view === 'orders' ? 'sales orders' : 'quotes')}
            </button>
            <button className="btn btn-sm btn-ghost" data-testid="si-drill-export-both" disabled={exporting || !d} onClick={() => exportPerson('both')}
              title="Excel export with both quotes and sales orders">both</button>
          </div>
          <button className="modal-close" data-testid="si-drill-close" onClick={onClose}>×</button>
        </div>
        {d && (
          <div className="si-drill-switch" role="tablist" data-testid="si-drill-switch">
            {[['quotes', 'Quotes', d.quotes], ['orders', 'Sales orders', d.orders]].map(([key, label, s]) => (
              <button key={key} role="tab" aria-selected={view === key}
                className={'si-drill-tab' + (view === key ? ' on' : '')} data-testid={'si-drill-tab-' + key}
                onClick={() => setView(key)}>
                <b>{label}</b>
                <span>{(s?.count || 0).toLocaleString()} · {fmtMoney(s?.amount, cur)}</span>
              </button>
            ))}
          </div>
        )}
        <div className="si-drill-body">
          {err && <div className="empty-note">Couldn't load breakdown: {err}</div>}
          {!d && !err && <div className="empty-note">Loading…</div>}
          {d && side && (
            <>
              <div className="si-rep-kpis">
                {view === 'quotes' ? <>
                  <div className="si-fc-kpi"><span className="si-fc-num">{d.quotes.count.toLocaleString()}</span><span className="si-fc-lbl">Quotes raised</span></div>
                  <div className="si-fc-kpi"><span className="si-fc-num">{fmtMoney(d.quotes.amount, cur)}</span><span className="si-fc-lbl">Total quoted</span></div>
                  <div className="si-fc-kpi"><span className="si-fc-num">{d.quotes.avg != null ? fmtMoney(d.quotes.avg, cur) : '—'}</span><span className="si-fc-lbl">Avg quote value</span></div>
                  <div className="si-fc-kpi">
                    <span className="si-fc-num" data-testid="si-drill-winrate">{d.win_rate_pct != null ? d.win_rate_pct + '%' : '—'}</span>
                    <span className="si-fc-lbl">{isClient ? 'Conversion' : 'Win rate'}{d.decided_count ? ' (' + d.won_count + '/' + d.decided_count + ' decided)' : ''}</span>
                  </div>
                  <div className="si-fc-kpi">
                    <span className="si-fc-num" data-testid="si-drill-truewin">{d.true_win_pct != null ? d.true_win_pct + '%' : '—'}</span>
                    <span className="si-fc-lbl">True wins (opportunity → order){d.true_win_count ? ' · ' + d.true_win_count + ' of ' + (d.opp_count || 0) + ' opps' : ''}</span>
                  </div>
                  <div className="si-fc-kpi">
                    <span className="si-fc-num" data-testid="si-drill-daystoclose">{d.avg_days_to_close != null ? Math.round(d.avg_days_to_close) + 'd' : '—'}</span>
                    <span className="si-fc-lbl">Avg time to close</span>
                  </div>
                  <div className="si-fc-kpi"><span className="si-fc-num">{d.quotes.gm_pct != null ? d.quotes.gm_pct + '%' : '—'}</span><span className="si-fc-lbl">Avg GM% est.</span></div>
                </> : <>
                  <div className="si-fc-kpi"><span className="si-fc-num">{d.orders.count.toLocaleString()}</span><span className="si-fc-lbl">Sales orders</span></div>
                  <div className="si-fc-kpi"><span className="si-fc-num">{fmtMoney(d.orders.amount, cur)}</span><span className="si-fc-lbl">Order value</span></div>
                  <div className="si-fc-kpi"><span className="si-fc-num">{d.orders.avg != null ? fmtMoney(d.orders.avg, cur) : '—'}</span><span className="si-fc-lbl">Avg order value</span></div>
                  <div className="si-fc-kpi"><span className="si-fc-num">{d.orders.gm_pct != null ? d.orders.gm_pct + '%' : '—'}</span><span className="si-fc-lbl">Avg GM% est.</span></div>
                  <div className="si-fc-kpi">
                    <span className="si-fc-num">{(d.true_win_count || 0).toLocaleString()}</span>
                    <span className="si-fc-lbl">Opportunities that became orders{d.true_win_amount ? ' · ' + fmtMoney(d.true_win_amount, cur) : ''}</span>
                  </div>
                </>}
              </div>
              {statusOrder.length > 0 && (
                <div className="si-rep-statuses" data-testid="si-drill-statuses">
                  {statusOrder.map(s => (
                    <span key={s} className={'si-status-chip ' + (STATUS_CLS[s] || (s === 'Billed' || s === 'Closed' ? 'won' : 'open'))}>{s}: {side.by_status[s]}</span>
                  ))}
                </div>
              )}
              {(side.monthly || []).length > 1 && (
                <SiBarChart testid={'si-drill-chart-' + unit} cur={cur} unit={unit}
                  tone={view === 'orders' ? 'amber' : 'navy'}
                  title={(view === 'orders' ? 'Sales orders' : 'Quotes') + ' per month'}
                  buckets={side.monthly.map(m => ({ key: m.month, count: m.count, amount: m.amount }))} />
              )}
              {(side.breakdown || []).length > 0 && (
                <div className="si-rep-clients" data-testid="si-drill-breakdown">
                  <h5>{isClient ? 'Top reps' : 'Top clients'} — {view === 'orders' ? 'by order value' : 'by quoted value'}</h5>
                  {side.breakdown.map(c => (
                    <div key={c.name} className="si-rep-client">
                      <span className="si-item-name" title={c.name}>{c.name}</span>
                      <span>{c.count} · <b>{fmtMoney(c.amount, cur)}</b>
                        <em className="si-share">{side.amount ? Math.round(c.amount / side.amount * 100) + '%' : ''}</em>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <h5 className="si-rep-recent-title">
                {view === 'orders' ? 'Sales orders' : 'Quotes'}
                <span className="si-rep-recent-count">
                  {side.shown < side.count ? 'showing the ' + side.shown + ' most recent of ' + side.count.toLocaleString() : 'all ' + side.count.toLocaleString()}
                </span>
              </h5>
              <div className="si-rep-recent">
                <table className="si-table">
                  <thead><tr>
                    <th>{view === 'orders' ? 'Order #' : 'Quote #'}</th><th>Date</th>
                    <th>{isClient ? 'Rep' : 'Client'}</th>
                    <th className="num">Amount</th><th className="num">GM%</th><th>Status</th>
                    <th>{view === 'orders' ? 'From quote' : 'Order'}</th>
                  </tr></thead>
                  <tbody>
                    {(side.recent || []).map((qt, i) => (
                      <tr key={qt.tranid + qt.trandate + i}>
                        <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{qt.tranid}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{qt.trandate}</td>
                        <td className="si-item-name" title={isClient ? qt.salesrep : qt.client}>{isClient ? (qt.salesrep || '—') : qt.client}</td>
                        <td className="num">{fmtMoney(qt.amount, cur)}</td>
                        <td className="num">{qt.gm_pct != null ? qt.gm_pct + '%' : '—'}</td>
                        <td><span className={'si-status-chip sm ' + (STATUS_CLS[qt.status] || (qt.status === 'Billed' || qt.status === 'Closed' ? 'won' : 'open'))}>{qt.status}</span></td>
                        <td className="si-link-cell">{qt.link || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(side.recent || []).length === 0 && <div className="empty-note" style={{ border: 'none' }}>No {unit}s in this window.</div>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
